import { Component, computed, DestroyRef, effect, inject, input, OnInit, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { distinctUntilChanged, debounceTime } from 'rxjs';
import { MatDatepicker, MatDatepickerModule, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe } from '@next/core-lib/i18n';

import {
  BaseCropFieldRegistration,
  VirusMonitorCropFieldRegistration,
} from '@app/shared/components/disease-overview-table.component/models/models';
import {
  type DateFieldConfig,
  DiseaseInsightToolbarComponent,
} from '@app/shared/components/disease-insight-toolbar.component/disease-insight-toolbar.component';
import { getPlantDateLimits } from 'src/app/shared/components/plant-date-warning.component/plant-date-warning.helpers';
import { DateService } from 'src/app/shared/services/date.service';
import { toUtcDate } from 'src/app/shared/utils';

@Component({
  selector: 'app-virus-monitor-insight-toolbar',
  standalone: true,
  templateUrl: './virus-monitor-insight-toolbar.component.html',
  styleUrl: './virus-monitor-insight-toolbar.component.scss',
  imports: [
    DiseaseInsightToolbarComponent,
    ReactiveFormsModule,
    MatFormField,
    MatDatepicker,
    MatDatepickerModule,
    MatDatepickerToggle,
    MatLabel,
    MatInput,
    MatSuffix,
    MatTooltip,
    TranslatePipe,
  ],
})
export class VirusMonitorInsightToolbarComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly dateService = inject(DateService);

  readonly selectedCropField = input.required<VirusMonitorCropFieldRegistration>();
  readonly isParticipatingToBem = input(false);
  readonly isReadOnly = input(false);

  readonly plantDateChanged = output<Date>();
  readonly emergenceDateChanged = output<Date>();
  readonly harvestDateChanged = output<Date>();
  readonly plantDateChangedInFuture = output<boolean>();
  readonly deadFoliageDateChanged = output<Date>();
  readonly sprayThresholdChanged = output<number>();

  protected readonly virusDeadFoliageDateFormControl = new FormControl<Date | null>(null);
  protected readonly virusSprayThresholdFormControl = new FormControl<number | null>(null);
  protected readonly plantDateLimits = getPlantDateLimits(this.dateService);
  protected readonly today = new Date();
  protected readonly plantDateFieldConfig = computed<DateFieldConfig>(() => ({
    labelKey: 'shared.diseaseDetail.plantDate',
    mode: this.isReadOnly() ? 'disabled' : 'editable',
    disabledReasonKey: this.isReadOnly() ? 'UI.CurrentYearOnlyMutationMessage' : undefined,
  }));
  protected readonly emergenceDateFieldConfig = computed<DateFieldConfig>(() => ({
    labelKey: 'shared.diseaseDetail.emergenceDate',
    mode: this.isReadOnly() ? 'disabled' : 'editable',
    disabledReasonKey: this.isReadOnly() ? 'UI.CurrentYearOnlyMutationMessage' : undefined,
  }));
  protected readonly harvestDateFieldConfig = computed<DateFieldConfig>(() => ({
    labelKey: 'shared.diseaseDetail.harvestDate',
    mode: this.isReadOnly() ? 'disabled' : 'editable',
    disabledReasonKey: this.isReadOnly() ? 'UI.CurrentYearOnlyMutationMessage' : undefined,
  }));

  constructor() {
    effect(() => {
      const selectedCropField = this.selectedCropField();
      this.virusDeadFoliageDateFormControl.setValue(selectedCropField.deadFoliageDate ?? null, { emitEvent: false });
      this.virusSprayThresholdFormControl.setValue(selectedCropField.sprayThreshold ?? null, { emitEvent: false });
    });

    effect(() => {
      if (this.isReadOnly()) {
        this.virusDeadFoliageDateFormControl.disable({ emitEvent: false });
        this.virusSprayThresholdFormControl.disable({ emitEvent: false });
      } else {
        this.virusDeadFoliageDateFormControl.enable({ emitEvent: false });
        this.virusSprayThresholdFormControl.enable({ emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    this.subscribeToDeadFoliageDateChanged();
    this.subscribeToSprayThresholdChanged();
  }

  protected asBaseRegistration(selectedCropField: VirusMonitorCropFieldRegistration): BaseCropFieldRegistration {
    return selectedCropField;
  }

  private subscribeToDeadFoliageDateChanged(): void {
    this.virusDeadFoliageDateFormControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe((newDate) => {
        const deadFoliageDate = toUtcDate(newDate);
        if (
          !deadFoliageDate ||
          !this.virusDeadFoliageDateFormControl.valid ||
          isSameDate(deadFoliageDate, this.selectedCropField().deadFoliageDate)
        ) {
          return;
        }

        this.deadFoliageDateChanged.emit(deadFoliageDate);
      });
  }

  private subscribeToSprayThresholdChanged(): void {
    this.virusSprayThresholdFormControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged(),
        debounceTime(600),
      )
      .subscribe((sprayThreshold) => {
        if (
          sprayThreshold == null ||
          !this.virusSprayThresholdFormControl.valid ||
          sprayThreshold === this.selectedCropField().sprayThreshold
        ) {
          return;
        }

        this.sprayThresholdChanged.emit(sprayThreshold);
      });
  }
}

function isSameDate(left?: Date | null, right?: Date | null): boolean {
  if (!left || !right) {
    return left == null && right == null;
  }

  return left.toISOString() === right.toISOString();
}
